import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
} from 'react-icons/fa';

const Footer: React.FC = () => {
  const { t } = useTranslation();

  const navLinks = [
    { name: t('navbar.home'), path: '/' },
    { name: t('navbar.about'), path: '/about' },
    { name: t('navbar.services'), path: '/services' },
    { name: t('navbar.projects'), path: '/projects' },
    { name: t('navbar.quote'), path: '/request-quote' },
  ];

  const serviceLinks = [
    { name: t('services_section.services.0.name1'), path: '/services/#steel' },
    {
      name: t('services_section.services.1.name2'),
      path: '/services/#portable',
    },
    {
      name: t('services_section.services.2.name3'),
      path: '/services/#aluminum',
    },
    {
      name: t('services_section.services.3.name4'),
      path: '/services/#kitchen',
    },
    {
      name: t('services_section.services.4.name5'),
      path: '/services/#advertising',
    },
    { name: t('services_section.services.5.name6'), path: '/services/#event' },
  ];

  return (
    <footer
      dir='ltr'
      className='relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden'
    >
      {/* Background Pattern */}
      <div className='absolute inset-0 opacity-5'>
        <div className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")]'></div>
      </div>

      <div className='relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12'>
          {/* Brand */}
          <div className='space-y-6'>
            <div>
              <h2 className='text-2xl font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 bg-clip-text text-transparent'>
                {t('footer.company.name')}
              </h2>
              <div className='w-12 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full mt-2'></div>
            </div>

            <p className='text-slate-300 leading-relaxed text-sm max-w-xs'>
              {t('footer.company.description')}
            </p>

            {/* Social Links */}
            <div className='flex space-x-4 justify-center'>
              <a
                href='#'
                className='w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-amber-400 hover:to-orange-500 transition-all duration-300 group'
              >
                <FaFacebook className='text-slate-300 group-hover:text-white transition-colors duration-300' />
              </a>
              <a
                href='#'
                className='w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-amber-400 hover:to-orange-500 transition-all duration-300 group'
              >
                <FaTwitter className='text-slate-300 group-hover:text-white transition-colors duration-300' />
              </a>
              <a
                href='https://www.instagram.com/lubeck_ksa/'
                target='_blank'
                className='w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-amber-400 hover:to-orange-500 transition-all duration-300 group'
              >
                <FaInstagram className='text-slate-300 group-hover:text-white transition-colors duration-300' />
              </a>
              <a
                href='#'
                className='w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-amber-400 hover:to-orange-500 transition-all duration-300 group'
              >
                <FaLinkedin className='text-slate-300 group-hover:text-white transition-colors duration-300' />
              </a>
            </div>
          </div>

          {/* Company */}
          <div className='space-y-6 text-center md:text-left'>
            <h3 className='text-lg font-semibold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent'>
              {t('footer.links.company_title')}
            </h3>
            <div className='space-y-3 flex flex-col items-center md:items-start'>
              {navLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.path}
                  className='text-sm text-slate-300 hover:text-white hover:translate-x-1 transition-all duration-200 relative group'
                >
                  <span className='relative z-10'>{link.name}</span>
                  <span className='absolute inset-0 w-0 bg-gradient-to-r from-amber-400/20 to-orange-400/20 group-hover:w-full transition-all duration-300 rounded'></span>
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className='space-y-6 text-center md:text-left'>
            <h3 className='text-lg font-semibold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent'>
              {t('footer.links.services_title')}
            </h3>
            <div className='space-y-3 flex flex-col items-center md:items-start'>
              {serviceLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.path}
                  className='text-sm text-slate-300 hover:text-white hover:translate-x-1 transition-all duration-200 relative group'
                >
                  <span className='relative z-10'>{link.name}</span>
                  <span className='absolute inset-0 w-0 bg-gradient-to-r from-amber-400/20 to-orange-400/20 group-hover:w-full transition-all duration-300 rounded'></span>
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className='space-y-6 text-center md:text-left'>
            <h3 className='text-lg font-semibold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent'>
              {t('footer.get_in_touch.title')}
            </h3>
            <div className='space-y-4 flex flex-col items-center md:items-start'>
              <div className='flex items-start space-x-3 group'>
                <FaMapMarkerAlt
                  className='text-amber-400 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform duration-200'
                  size={16}
                />
                <p className='text-sm text-slate-300 leading-relaxed'>
                  {t('footer.get_in_touch.address')}
                </p>
              </div>

              <div className='flex items-center space-x-3 group'>
                <FaPhone
                  className='text-amber-400 flex-shrink-0 group-hover:scale-110 transition-transform duration-200'
                  size={16}
                />
                <a
                  href={`tel:${t('footer.get_in_touch.phone')}`}
                  className='text-sm text-slate-300 hover:text-white transition-colors duration-200'
                >
                  {t('footer.get_in_touch.phone')}
                </a>
              </div>

              <div className='flex items-center space-x-3 group'>
                <FaEnvelope
                  className='text-amber-400 flex-shrink-0 group-hover:scale-110 transition-transform duration-200'
                  size={16}
                />
                <a
                  href='mailto:info@lubeckinteriors.com'
                  className='text-sm text-slate-300 hover:text-white transition-colors duration-200'
                >
                  info@lubeckinteriors.com
                </a>
              </div>

              <div className='flex items-center space-x-3 group'>
                <FaEnvelope
                  className='text-amber-400 flex-shrink-0 group-hover:scale-110 transition-transform duration-200'
                  size={16}
                />
                <a
                  href={`mailto:${t('footer.get_in_touch.email')}`}
                  className='text-sm text-slate-300 hover:text-white transition-colors duration-200'
                >
                  {t('footer.get_in_touch.email')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className='relative border-t border-slate-700/50 bg-slate-900/50 backdrop-blur-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col md:flex-row justify-between items-center gap-4'>
          <p className='text-sm text-slate-400 text-center md:text-left'>
            {t('footer.copyright')}
          </p>

          <div className='flex items-center gap-4'>
            <p className='text-sm text-slate-400'>
              Developed by{' '}
              <a
                href='https://www.taphubs.org'
                target='_blank'
                rel='noreferrer'
                className='hover:text-amber-400 transition-colors'
              >
                www.taphubs.org
              </a>
            </p>

            <p className='text-sm text-slate-400 border-l border-slate-600 pl-4'>
              Version 2.0.4
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
